import React, { useState } from 'react';
import { formatArticleDate, getCategoryName, getCategoryTextClass, getCategoryHoverClass, getCategoryBadgeClass, getCategoryClass } from '../utils/helpers';
import { ClockIcon } from './Icons';
import { getTextClampClasses } from '../styles/responsiveUtils';

const ArticleCard = ({ 
  article, 
  horizontal = false, 
  featured = false,
  compact = false,
  className = '',
  showCategory = true,
  currentCategoryPage = null,
  showExcerpt = true,
  showDescription = true,
  layout = 'vertical'
}) => {
  // Move useState hook to the top level before conditional returns
  const [imageError, setImageError] = useState(false);
  
  if (!article) return null;
  
  const { 
    id,
    title, 
    excerpt, 
    description,
    coverImage, 
    imageUrl,
    image,
    category, 
    slug, 
    publishDate,
    readTime,
    date,
    createTime,
    duration
  } = article;

  // Don't show category badge if we're on that category page
  const shouldShowCategory = showCategory && (!currentCategoryPage || currentCategoryPage !== category);
  
  // Use formatArticleDate instead of date-fns
  const formattedDate = formatArticleDate(publishDate || createTime || date) || '';
  
  // Use direct URL instead of imported placeholder
  const placeholderImage = "https://images.unsplash.com/photo-1504674900247-0877df9cc836?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80";
  
  // Handle image error
  const handleImageError = () => {
    setImageError(true);
  };
  
  // 修复图片URL获取逻辑：依次尝试获取imageUrl、coverImage或image字段
  const getImageUrl = () => {
    if (imageUrl) return imageUrl;
    if (coverImage) return coverImage;
    if (image) return image;
    return null;
  };
  
  const articleImageUrl = getImageUrl();
  const displayImageUrl = imageError ? placeholderImage : articleImageUrl;
  
  // Get category-specific styling
  const categoryClass = getCategoryClass(category);
  const categoryTextClass = getCategoryTextClass(category);
  const categoryHoverClass = getCategoryHoverClass(category);
  
  // Get read time display
  const displayReadTime = readTime || duration || '5 min read';
  const formattedReadTime = displayReadTime.includes('min') ? displayReadTime : `${displayReadTime} min read`;
  
  // Get description/excerpt
  const displayExcerpt = description || excerpt || '';

  // Horizontal layout (for trending lists)
  if (horizontal || layout === 'horizontal') {
    return (
      <div className={`article-card-horizontal bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group ${className}`}>
        <a href={`/article.html?id=${id || slug}`} className="flex items-stretch group w-full">
          <div className="relative w-32 md:w-36 lg:w-40 flex-shrink-0 overflow-hidden rounded-lg">
            <img 
              src={displayImageUrl}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={handleImageError}
              loading="lazy"
            />
            {shouldShowCategory && (
              <div className="absolute top-1 left-1">
                <span className={`inline-block px-1.5 py-0.5 text-[8px] font-medium rounded-md shadow-sm ${getCategoryBadgeClass(getCategoryName(category))}`}>
                  {getCategoryName(category)}
                </span>
              </div>
            )}
          </div>
          
          <div className="p-3 flex flex-col justify-center flex-grow min-w-0">
            <div className="flex-1">
              <h2 className="text-sm md:text-base font-bold text-stone-900 mb-2 hover:text-slate-600 transition-colors line-clamp-2 leading-tight">
                {title}
              </h2>
              
              {showDescription && showExcerpt && displayExcerpt && (
                <p className="text-stone-500 text-xs mb-2 line-clamp-2 leading-relaxed">
                  {displayExcerpt}
                </p>
              )}
            </div>
            
            <div className="flex items-center text-[10px] text-stone-400">
              <span>{formattedDate}</span>
              <span className="mx-1.5">•</span>
              <span className="flex items-center">
                <ClockIcon className="w-3 h-3 mr-0.5" /> 
                {formattedReadTime}
              </span>
            </div>
          </div>
        </a>
      </div>
    );
  }
  
  // Featured layout (larger card for homepage hero)
  if (featured) {
    return (
      <a 
        href={`/article.html?id=${id || slug}`}
        className={`block group relative bg-white rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md ${className}`}
      >
        <div className="aspect-w-16 aspect-h-7 relative">
          <img
            src={displayImageUrl}
            alt={title}
            onError={handleImageError}
            className="w-full h-full object-cover"
          />
          {shouldShowCategory && (
            <div className="absolute top-2 left-2 z-10">
              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md shadow-sm ${getCategoryBadgeClass(getCategoryName(category))}`}>
                {getCategoryName(category)}
              </span>
            </div>
          )}
        </div>
        <div className="px-3 sm:px-4 md:px-5 flex flex-col">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-900 mb-2 hover:text-slate-600 transition-colors line-clamp-2">
            {title}
          </h2>
          {showExcerpt && displayExcerpt && (
            <p className="text-gray-600 text-sm sm:text-base mb-2 line-clamp-2 md:line-clamp-3">
              {displayExcerpt}
            </p>
          )}
          <div className="text-xs sm:text-sm text-gray-500 flex items-center mt-auto">
            <span>{formattedDate}</span>
            <span className="mx-2">•</span>
            <span className="flex items-center">
              <ClockIcon className="w-3 h-3 mr-1" /> 
              {formattedReadTime}
            </span>
          </div>
        </div>
      </a>
    );
  }
  
  // Compact layout (for sidebar widget)
  if (compact) {
    return (
      <a 
        href={`/article.html?id=${id || slug}`}
        className={`flex group items-start py-3 ${className}`}
      >
        <div className="w-16 h-12 sm:w-20 sm:h-16 flex-shrink-0 rounded overflow-hidden mr-3 relative">
          <img
            src={displayImageUrl}
            alt={title}
            onError={handleImageError}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-grow min-w-0">
          <h3 className="text-sm sm:text-base font-semibold text-stone-900 hover:text-slate-600 transition-colors line-clamp-2 leading-tight">
            {title}
          </h3>
          <div className="text-xs text-gray-500 mt-1">
            {formattedDate}
          </div>
        </div>
      </a>
    );
  }
  
  // Default vertical card layout
  return (
    <div className={`article-card bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col ${className}`}>
      <a href={`/article.html?id=${id || slug}`} className="group h-full flex flex-col">
        <div className="relative overflow-hidden aspect-[16/9] sm:aspect-[2/1]">
          <img 
            src={displayImageUrl}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={handleImageError}
          />
          {shouldShowCategory && (
            <div className="absolute top-3 left-3">
              <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-md shadow-sm ${getCategoryBadgeClass(getCategoryName(category))}`}>
                {getCategoryName(category)}
              </span>
            </div>
          )}
        </div>
        
        <div className="p-4 flex-1 flex flex-col">
          <h2 className="text-lg font-bold text-stone-900 mb-3 line-clamp-2 group-hover:text-slate-600 transition-colors">
            {title}
          </h2>
          
          {showExcerpt && displayExcerpt && (
            <p className="text-stone-600 text-sm mb-4 line-clamp-3 flex-1">
              {displayExcerpt}
            </p>
          )}
          
          <div className="text-xs text-stone-500 flex items-center">
            <span>{formattedDate}</span>
            <span className="mx-2">•</span>
            <span className="flex items-center">
              <ClockIcon className="w-3 h-3 mr-1" /> 
              {formattedReadTime}
            </span>
          </div>
        </div>
      </a>
    </div>
  );
};

export default ArticleCard; 